import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, formatPence } from './ordering/orderingApi';
import usePageTitle from '@/hooks/usePageTitle';

export default function OrderConfirmedPage() {
  usePageTitle('Order Confirmed');
  const [params] = useSearchParams();
  const orderId = params.get('order');

  const [settings, setSettings] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [raffleSent, setRaffleSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetch = async () => {
      try {
        const [s, o] = await Promise.all([
          axios.get(`${API}/order/public-settings`),
          axios.get(`${API}/order/${orderId}`),
        ]);
        if (cancelled) return;
        setSettings(s.data);
        setOrder(o.data);
      } catch {
        if (!cancelled) toast.error('Order not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    // Poll a couple of times in case reconciliation hasn't run yet
    const t = setInterval(fetch, 5000);
    setTimeout(() => clearInterval(t), 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [orderId]);

  const branding = settings?.order_page_branding || {};
  const showRaffle = settings && !settings.raffle_upsell_disabled;
  const isPaid = order?.status && order.status !== 'awaiting_payment';

  const handleRaffle = async (e) => {
    e.preventDefault();
    if (!/^\+?[0-9 ()\-]{7,20}$/.test(phone.trim())) return toast.error('Enter a valid phone number');
    setSubmitting(true);
    try {
      await axios.post(`${API}/order/raffle-signup`, {
        order_id: orderId,
        phone: phone.trim(),
      });
      setRaffleSent(true);
      toast.success('Raffle entry confirmed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sign up');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading…</div>;
  if (!order) return <div className="p-10 text-center">Order not found.</div>;

  return (
    <div className="min-h-screen" style={{ background: branding.background_color, color: branding.text_color }}>
      <header className="py-8 flex flex-col items-center gap-2">
        {branding.logo_url && <img src={branding.logo_url} alt="Event" className="h-14 object-contain" />}
      </header>

      <main className="max-w-xl mx-auto px-4 space-y-6">
        <section className="rounded-2xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {isPaid ? (
            <>
              <h1 className="text-3xl font-bold mb-2">Thanks {order.first_name}! 🎉</h1>
              <p className="opacity-90">Your order is on its way to <strong>seat {order.seat_number}</strong>.</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2">Awaiting payment</h1>
              <p className="opacity-90">If you've just paid, hang on — we're confirming with SumUp.</p>
            </>
          )}
          <div className="mt-4 text-left">
            <div className="text-xs uppercase opacity-70 mb-1">Your order</div>
            <ul className="space-y-1">
              {(order.items || []).map((it, i) => (
                <li key={i} className="flex justify-between text-sm">
                  <span>
                    {it.quantity}× {it.product_name}
                    {it.choices?.length > 0 && (
                      <span className="opacity-70"> — {it.choices.map((c) => c.choice_name).join(', ')}</span>
                    )}
                  </span>
                  <span>{formatPence(it.line_total_pence)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t mt-2 pt-2 flex justify-between font-bold">
              <span>Total</span>
              <span>{formatPence(order.total_pence)}</span>
            </div>
          </div>
        </section>

        {showRaffle && isPaid && !raffleSent && (
          <section className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <h2 className="font-bold mb-2">🎟 Enter the event raffle</h2>
            <p className="text-sm opacity-80 mb-3">Enter your mobile number for a confirmation SMS.</p>
            <form onSubmit={handleRaffle} className="flex gap-2">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+447…"
                className="flex-1 px-3 py-2 rounded-lg text-black"
                data-testid="raffle-phone"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg font-bold disabled:opacity-50"
                style={{ background: branding.button_color, color: branding.button_text_color }}
                data-testid="raffle-submit"
              >
                {submitting ? 'Sending…' : 'Confirm'}
              </button>
            </form>
          </section>
        )}

        {raffleSent && (
          <p className="text-center opacity-90">Raffle entry sent — good luck!</p>
        )}
      </main>
    </div>
  );
}
